import { Edge } from "@xyflow/react";

import { analyzeDisconnectedNodes } from "@signalist/shared-types";

import { CampaignNodeTypes } from "../config/node-types";
import { MyNode } from "../types/node-data.types";

/** What the builder knows about the campaign beyond its graph. */
export interface CampaignDecorationContext {
	/** None of the campaign's LinkedIn senders can send InMail (PREC-3897). */
	inmailSenderMissing: boolean;
}

/**
 * The flags every node carries for drawing, derived from the graph and the campaign: its
 * neighbours, whether it is cut off from the start or is a place an orphan chain can reattach, and
 * the InMail sender caveat. Pure: the builder stores the result only when it differs.
 */
export const decorateCampaignNodes = (
	nodes: MyNode[],
	edges: Edge[],
	context: CampaignDecorationContext
): MyNode[] => {
	const analysis = analyzeDisconnectedNodes(nodes, edges);
	const orphanRootSet = new Set(analysis.orphanRootIds);
	const openSourceSet = (handle: string) =>
		new Set(analysis.openSources.filter((s) => s.handle === handle).map((s) => s.nodeId));
	const openNormalSet = openSourceSet("normalEdge");
	const openYesSet = openSourceSet("yesEdge");
	const openNoSet = openSourceSet("noEdge");

	return nodes.map((node) => {
		const source = edges.find((edge) => edge.target === node.id)?.source;
		const target = edges.find((edge) => edge.source === node.id)?.target;

		const isDisconnected =
			(node.type as CampaignNodeTypes) === "starterNode" && analysis.starterDisconnected;

		return {
			...node,
			data: {
				...node.data,
				target,
				source,
				isDisconnected,
				// Any orphan chain root (regardless of starter state) is a valid reconnection target.
				isDisconnectedTarget: orphanRootSet.has(node.id),
				// Expose drag handles only on reachable nodes while an orphan exists. The starter's own
				// "disconnected" state already shows its handle, so don't double up when it is set.
				hasOpenSource: !isDisconnected && openNormalSet.has(node.id),
				hasOpenYesSource: openYesSet.has(node.id),
				hasOpenNoSource: openNoSet.has(node.id),
				inmailSenderMissing:
					(node.type as CampaignNodeTypes) === "linkedinInmailNode" && context.inmailSenderMissing,
			},
		};
	});
};
